import { Citation } from "../../../platform";

export type { Citation };

export interface ParsedResponse {
  /** The assistant's text with `<segment=XX>` tags replaced by Markdown cite links */
  content: string;
  /** Unique citations in order of first appearance */
  citations: Citation[];
}

const CLOSE = String.raw`\\?<\/segment\s*\\?>`;

/**
 * The tag as models actually write it, not only as the prompt asks for it. Some close it
 * like XML (`<segment=X></segment>`), run the two together (`<segment=X</segment>`, with
 * no `>`), or space it (`<segment = X>`), and a markdown-minded one escapes the brackets
 * (`\<segment=X\>`). Each of those reached the user as raw markup, so they are read as
 * the one tag they mean.
 */
const tagPattern = (segmentIdPattern: string, capture: boolean) =>
  new RegExp(
    String.raw`\\?<segment\s*=\s*(${capture ? "" : "?:"}${segmentIdPattern})\s*` +
      String.raw`(?:\/?\\?>(?:\s*${CLOSE})?|${CLOSE})`,
    "g"
  );

/** A closing tag left over from a model that wrapped its claim, `<segment=X>claim</segment>`. */
const STRAY_CLOSE = new RegExp(CLOSE, "g");

/**
 * Parses an assistant response containing inline `<segment=XX>` tags.
 *
 * The tag convention is shared across hosts; the ID vocabulary inside it is not --
 * Word emits paragraph and table-cell IDs, another host will emit its own. So the
 * caller passes the adapter's pattern rather than this module knowing them all.
 *
 * Each unique segment ID is assigned a sequential number (starting from 1). The tags
 * are replaced with Markdown links `[[n]](#cite-n)` so that any Markdown renderer can
 * handle them -- we just override the `a` component to intercept fragment URLs.
 *
 * Duplicate references to the same segment reuse the same number.
 */
export const parseCitations = (raw: string, segmentIdPattern: string): ParsedResponse => {
  const citations: Citation[] = [];
  const seen = new Map<string, number>(); // segmentId → citation index (1-based)

  const content = raw.replace(tagPattern(segmentIdPattern, true), (_match, segmentId: string) => {
    let num = seen.get(segmentId);
    if (num === undefined) {
      num = citations.length + 1;
      seen.set(segmentId, num);
      citations.push({ segmentId, label: segmentId });
    }
    return `[[${num}]](#cite-${num})`;
  });

  return { content: content.replace(STRAY_CLOSE, "").trim(), citations };
};

/**
 * Remove the tags without turning them into anything.
 *
 * For a host that cannot navigate: there, a citation has nowhere to lead, so the chat
 * offers none -- and the numbered markers parseCitations leaves behind would be worse
 * than useless, a reference to a list that is not shown. The model is asked not to cite
 * in that case, and this is what makes a tag it emits anyway disappear cleanly rather
 * than surfacing as raw `<segment=b3>` in the middle of a sentence.
 */
export const stripCitationTags = (raw: string, segmentIdPattern: string): string =>
  raw.replace(tagPattern(segmentIdPattern, false), "").replace(STRAY_CLOSE, "").trim();

/**
 * A reply as text worth pasting somewhere else: the markdown with the numbered
 * citation links parseCitations wrote taken back out.
 *
 * A pasted `[[2]](#cite-2)` leads nowhere outside this pane -- the anchor is a segment
 * of the document open here -- so copying keeps the words and drops the pointers.
 * A space before a marker goes with it, so "the date [1]." pastes as "the date.".
 */
export const stripCitationMarkers = (content: string): string =>
  content.replace(/ ?\[\[\d+\]\]\(#cite-\d+\)/g, "").trim();
